import type { Rng } from './rng';
import { blockSeconds, carrierSpeed, rollTackle, type RunMove, type TackleRoll } from './running';
import type { Tunables } from './tunables';
import type { Player } from './types';

/**
 * The live run: a ball carrier, his blockers and the pursuing defense, stepped at a fixed rate.
 * Used after a catch, on a handoff, and on an interception return. Coordinates are ball-relative
 * yards (x right, y downfield for the offense). The same seed and the same inputs on the same steps
 * always give the same run.
 */

export type ActorState = 'free' | 'engaged' | 'down' | 'resting';

export interface Actor {
  player: Player;
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Top speed, yards per second. */
  speed: number;
  state: ActorState;
  /** Seconds left in the current state (engaged, down, resting) or before reacting. */
  timer: number;
  react: number;
  /** Index into the other side's actors while engaged, else -1. */
  partner: number;
  /** Squared up on the carrier before a tackle is allowed: he keeps this offset from him. */
  shadow: { x: number; y: number } | null;
}

export interface RacInput {
  /** Stick direction in field terms: x right, y upfield (for the carrier). Length 0 to 1. */
  stickX: number;
  stickY: number;
  sprint: boolean;
  /** A move started on this step, or null. */
  move: RunMove | null;
}

export type RacEvent =
  | { kind: 'broke'; defender: number; tackle: TackleRoll }
  | { kind: 'tackled'; defender: number; tackle: TackleRoll }
  | { kind: 'touchdown' }
  | { kind: 'outOfBounds' }
  | { kind: 'safety' }
  | { kind: 'move'; move: RunMove; free: boolean };

export interface RacSetup {
  carrier: Player;
  carrierPos: { x: number; y: number };
  carrierVel: { x: number; y: number };
  /** +1: the carrier runs toward +y (offense). -1: toward -y (interception return). */
  dir: 1 | -1;
  /** Players on the carrier's side who block (positions ball-relative). */
  blockers: { player: Player; x: number; y: number }[];
  /** Players chasing the carrier. */
  defenders: { player: Player; x: number; y: number; moving?: boolean }[];
  /** Yard line (0 to 100, from the offense's own goal) the play started on. */
  ballOn: number;
  /** Where to run with no input before heading upfield (handoffs and tosses), or null. */
  aim: { x: number; y: number } | null;
  /** True: no player input, the carrier runs himself (interception return, tests). */
  auto: boolean;
  /** No tackle before this many seconds into the run: defenders close in and break down first (after a catch). */
  tackleAfter?: number;
  rng: Rng;
  t: Tunables;
}

export const NO_INPUT: RacInput = { stickX: 0, stickY: 0, sprint: false, move: null };

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

function makeActor(player: Player, x: number, y: number, speed: number): Actor {
  return { player, x, y, vx: 0, vy: 0, speed, state: 'free', timer: 0, react: 0, partner: -1, shadow: null };
}

export class RacWorld {
  readonly carrier: Actor;
  readonly blockers: Actor[];
  readonly defenders: Actor[];
  /** Seconds since the run started. */
  time = 0;
  stamina = 100;
  /** The last move and when it started. */
  move: RunMove | null = null;
  moveAt = -Infinity;
  /** Heading of the carrier in radians, 0 = upfield. */
  heading = 0;
  over = false;
  /** Where the ball is spotted when the run ends (ball-relative y). */
  spotY = 0;
  readonly log: RacEvent[] = [];
  private jukeDir = 0;
  private slowLeft = 0;
  private aimDone = false;
  private acc = 0;
  private queuedMove: RunMove | null = null;
  private readonly halfWidth: number;
  private readonly tackleAfter: number;

  constructor(private readonly s: RacSetup) {
    const t = s.t;
    this.halfWidth = t.field.widthYards / 2;
    this.tackleAfter = s.tackleAfter ?? 0;
    this.carrier = makeActor(s.carrier, s.carrierPos.x, s.carrierPos.y, carrierSpeed(s.carrier, false, t));
    this.carrier.vx = s.carrierVel.x;
    this.carrier.vy = s.carrierVel.y;
    const sp = Math.hypot(s.carrierVel.x, s.carrierVel.y);
    this.heading = sp > 0.1 ? Math.atan2(s.carrierVel.x, s.carrierVel.y * s.dir) : 0;
    const speedOf = (p: Player) => (t.movement.baseSpeedYps + t.movement.perSpdYps * p.ratings.SPD) * t.run.pursuitSpeedFactor;
    this.blockers = s.blockers.map((b) => makeActor(b.player, b.x, b.y, speedOf(b.player)));
    this.defenders = s.defenders.map((d) => {
      const a = makeActor(d.player, d.x, d.y, speedOf(d.player));
      const dx = s.carrierPos.x - d.x;
      const dy = s.carrierPos.y - d.y;
      const dist = Math.hypot(dx, dy);
      // Close defenders are already on him; far ones take a moment to read it. All start moving toward the ball.
      a.react = dist < t.run.closeYards ? 0 : t.run.reactionSeconds + dist * t.run.reactionPerYard;
      if (dist > 0.01 && d.moving) {
        a.vx = (dx / dist) * a.speed * t.run.startSpeedFactor;
        a.vy = (dy / dist) * a.speed * t.run.startSpeedFactor;
      }
      return a;
    });
    this.spotY = s.carrierPos.y;
    this.aimDone = s.aim === null;
  }

  /** Field yard line of the carrier (0 to 100 from the offense's goal). */
  yardLine(): number {
    return this.s.ballOn + this.carrier.y;
  }

  /**
   * Advances by real seconds using fixed steps. Input applies to every step in this call; a move fires on
   * the first step that runs. A move given on a call that runs no step (a short frame, slow motion, a hit
   * stop) waits for the next step instead of being lost.
   */
  advance(seconds: number, input: RacInput): RacEvent[] {
    const out: RacEvent[] = [];
    const step = this.s.t.run.fixedStepSeconds;
    this.acc += seconds;
    if (input.move) this.queuedMove = input.move;
    while (this.acc >= step && !this.over) {
      this.acc -= step;
      const move = this.queuedMove;
      this.queuedMove = null;
      this.step(step, move === input.move ? input : { ...input, move }, out);
    }
    return out;
  }

  step(dt: number, input: RacInput, out: RacEvent[]): void {
    if (this.over) return;
    const t = this.s.t;
    const r = t.run;
    const c = this.carrier;
    const dir = this.s.dir;
    this.time += dt;

    // Moves.
    if (input.move) {
      const free = this.stamina >= r.moveStaminaCost;
      if (free) {
        this.stamina -= r.moveStaminaCost;
        this.move = input.move;
        this.moveAt = this.time;
        this.jukeDir = input.move === 'jukeLeft' ? -1 : input.move === 'jukeRight' ? 1 : 0;
      }
      const ev: RacEvent = { kind: 'move', move: input.move, free };
      out.push(ev);
      this.log.push(ev);
    }
    const moveAge = this.time - this.moveAt;

    // Steering: stick, or auto (aim point, then upfield away from the nearest tackler).
    let want = this.heading;
    const mag = Math.hypot(input.stickX, input.stickY);
    if (!this.s.auto && mag > r.stickDeadzone) {
      want = Math.atan2(input.stickX, input.stickY);
    } else if (!this.aimDone && this.s.aim) {
      const dx = this.s.aim.x - c.x;
      const dy = (this.s.aim.y - c.y) * dir;
      if (Math.hypot(dx, dy) < 1 || dy < 0) this.aimDone = true;
      else want = Math.atan2(dx, dy);
    } else if (this.s.auto) {
      want = this.autoHeading();
    } else {
      // No touch: ease back toward straight upfield.
      want = this.heading * 0.9;
    }
    let d = want - this.heading;
    while (d > Math.PI) d -= 2 * Math.PI;
    while (d < -Math.PI) d += 2 * Math.PI;
    this.heading += clamp(d, -r.turnRateRadPerSecond * dt, r.turnRateRadPerSecond * dt);

    // Speed.
    const sprint = input.sprint && this.stamina > 0;
    if (sprint) this.stamina = Math.max(0, this.stamina - r.staminaDrainPerSecond * dt);
    let top = carrierSpeed(c.player, sprint, t);
    if (this.move === 'spin' && moveAge < r.spinSeconds) top *= r.spinSpeedFactor;
    if (this.slowLeft > 0) {
      this.slowLeft -= dt;
      top *= r.brokenSlowFactor;
    }
    const tvx = Math.sin(this.heading) * top;
    const tvy = Math.cos(this.heading) * top * dir;
    const maxDv = r.accelYps2 * dt;
    c.vx += clamp(tvx - c.vx, -maxDv, maxDv);
    c.vy += clamp(tvy - c.vy, -maxDv, maxDv);
    let jx = 0;
    if (this.jukeDir !== 0 && moveAge < r.jukeSeconds) jx = (this.jukeDir * r.jukeYards) / r.jukeSeconds;
    c.x += (c.vx + jx) * dt;
    c.y += c.vy * dt;

    this.stepBlockers(dt);
    this.stepDefenders(dt);

    // End conditions.
    const line = this.yardLine();
    if (dir === 1 && line >= 100) return this.end({ kind: 'touchdown' }, c.y, out);
    if (dir === -1 && line <= 0) return this.end({ kind: 'touchdown' }, c.y, out);
    if (Math.abs(c.x) > this.halfWidth) return this.end({ kind: 'outOfBounds' }, c.y, out);
    if (this.time >= r.maxSeconds) return this.end({ kind: 'outOfBounds' }, c.y, out);

    // Contact.
    if (this.time < this.tackleAfter) return;
    for (let i = 0; i < this.defenders.length; i++) {
      const a = this.defenders[i]!;
      if (a.state !== 'free' || a.react > 0) continue;
      if (Math.hypot(a.x - c.x, a.y - c.y) > r.contactRadiusYards) continue;
      let extra = 0;
      for (let j = 0; j < this.defenders.length; j++) {
        const b = this.defenders[j]!;
        if (j !== i && b.state === 'free' && Math.hypot(b.x - c.x, b.y - c.y) <= r.gangRadiusYards) extra++;
      }
      const tackle = rollTackle(
        { carrier: c.player, tackler: a.player, move: this.move, moveAge, stamina: this.stamina, extraDefenders: extra },
        this.s.rng,
        t,
      );
      if (tackle.broke) {
        a.state = 'down';
        a.timer = r.brokenStumbleSeconds;
        this.slowLeft = r.brokenSlowSeconds;
        this.stamina = Math.max(0, this.stamina - r.breakStaminaCost);
        this.move = null;
        const ev: RacEvent = { kind: 'broke', defender: i, tackle };
        out.push(ev);
        this.log.push(ev);
      } else {
        const fall = this.move === 'truck' && moveAge <= r.timedWindowSeconds ? r.truckFallYards : r.forwardFallYards;
        a.state = 'down';
        a.timer = Infinity;
        const spot = c.y + fall * dir;
        // A tackle in your own end zone is a safety.
        if (dir === 1 && this.s.ballOn + spot <= 0) return this.end({ kind: 'safety' }, spot, out);
        if (dir === 1 && this.s.ballOn + spot >= 100) return this.end({ kind: 'touchdown' }, spot, out);
        return this.end({ kind: 'tackled', defender: i, tackle }, spot, out);
      }
    }
  }

  private end(ev: RacEvent, spotY: number, out: RacEvent[]): void {
    this.over = true;
    this.spotY = spotY;
    out.push(ev);
    this.log.push(ev);
  }

  /** Auto carrier: upfield, leaning away from the nearest free defender ahead. */
  private autoHeading(): number {
    const c = this.carrier;
    const dir = this.s.dir;
    let best: Actor | null = null;
    let bestD = Infinity;
    for (const a of this.defenders) {
      if (a.state !== 'free') continue;
      const ahead = (a.y - c.y) * dir;
      if (ahead < -1) continue;
      const d = Math.hypot(a.x - c.x, a.y - c.y);
      if (d < bestD) {
        bestD = d;
        best = a;
      }
    }
    if (!best || bestD > 8) return -c.x * 0.02;
    const side = best.x > c.x ? -1 : 1;
    return clamp(side * (1.2 - bestD / 8), -1.1, 1.1);
  }

  private stepBlockers(dt: number): void {
    const r = this.s.t.run;
    const c = this.carrier;
    for (let i = 0; i < this.blockers.length; i++) {
      const b = this.blockers[i]!;
      if (b.state === 'engaged') {
        const d = this.defenders[b.partner];
        b.timer -= dt;
        if (!d || b.timer <= 0) {
          b.state = 'resting';
          b.timer = r.blockerRestSeconds;
          if (d && d.state === 'engaged') {
            d.state = 'free';
            d.react = r.reactionSeconds;
          }
          b.partner = -1;
          continue;
        }
        // Locked together, drifting toward the carrier.
        const dx = c.x - d.x;
        const dy = c.y - d.y;
        const len = Math.hypot(dx, dy) || 1;
        d.x += (dx / len) * r.blockDriftYps * dt;
        d.y += (dy / len) * r.blockDriftYps * dt;
        b.x = d.x - (dx / len) * 0.7;
        b.y = d.y - (dy / len) * 0.7;
        b.vx = d.vx = (dx / len) * r.blockDriftYps;
        b.vy = d.vy = (dy / len) * r.blockDriftYps;
        continue;
      }
      if (b.state === 'resting') {
        b.timer -= dt;
        if (b.timer <= 0) b.state = 'free';
      }
      // Seek the nearest free defender within range and get between him and the carrier.
      let target = -1;
      let bestD = r.blockSeekRadiusYards;
      for (let j = 0; j < this.defenders.length; j++) {
        const d = this.defenders[j]!;
        if (d.state !== 'free') continue;
        const dist = Math.hypot(d.x - b.x, d.y - b.y);
        if (dist < bestD) {
          bestD = dist;
          target = j;
        }
      }
      let tx = b.x + c.vx * 0.5;
      let ty = b.y + c.vy * 0.5;
      if (target >= 0) {
        const d = this.defenders[target]!;
        tx = d.x + (c.x - d.x) * 0.25;
        ty = d.y + (c.y - d.y) * 0.25;
        if (b.state === 'free' && bestD <= r.blockEngageRadiusYards) {
          b.state = 'engaged';
          b.partner = target;
          b.timer = blockSeconds(b.player, d.player, this.s.t);
          d.state = 'engaged';
          d.partner = i;
          continue;
        }
      }
      this.moveToward(b, tx, ty, dt);
    }
  }

  private stepDefenders(dt: number): void {
    const r = this.s.t.run;
    const c = this.carrier;
    for (const a of this.defenders) {
      if (a.state === 'engaged') continue;
      if (a.state === 'down') {
        a.vx *= 0.9;
        a.vy *= 0.9;
        a.timer -= dt;
        if (a.timer <= 0) {
          a.state = 'free';
          a.react = r.reactionSeconds;
        }
        continue;
      }
      if (a.react > 0) {
        a.react -= dt;
        a.vx *= 0.9;
        a.vy *= 0.9;
        continue;
      }
      const dist = Math.hypot(c.x - a.x, c.y - a.y);
      if (this.time < this.tackleAfter) {
        // Too soon to tackle: once he is close he squares up a step away and stays with the carrier, ready to hit.
        if (!a.shadow && dist <= r.breakdownEngageYards) a.shadow = { x: a.x - c.x, y: a.y - c.y };
        if (a.shadow) {
          // Ease to a step away rather than jump there.
          const len = Math.max(Math.hypot(a.shadow.x, a.shadow.y), 0.01);
          const k = (len + clamp(r.breakdownYards - len, -r.breakdownEaseYps * dt, r.breakdownEaseYps * dt)) / len;
          a.shadow.x *= k;
          a.shadow.y *= k;
          a.x = c.x + a.shadow.x;
          a.y = c.y + a.shadow.y;
          a.vx = c.vx;
          a.vy = c.vy;
          continue;
        }
      } else a.shadow = null;
      const lead = clamp((dist / a.speed) * r.pursuitLeadFactor, 0, r.pursuitLeadMaxSeconds);
      this.moveToward(a, c.x + c.vx * lead, c.y + c.vy * lead, dt);
    }
  }

  private moveToward(a: Actor, tx: number, ty: number, dt: number): void {
    const r = this.s.t.run;
    const dx = tx - a.x;
    const dy = ty - a.y;
    const len = Math.hypot(dx, dy);
    const sp = len > 0.05 ? a.speed : 0;
    const tvx = len > 0 ? (dx / len) * sp : 0;
    const tvy = len > 0 ? (dy / len) * sp : 0;
    const maxDv = r.accelYps2 * dt;
    a.vx += clamp(tvx - a.vx, -maxDv, maxDv);
    a.vy += clamp(tvy - a.vy, -maxDv, maxDv);
    a.x += a.vx * dt;
    a.y += a.vy * dt;
  }
}
